import { useState, useEffect } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { ShoppingBag, ChevronLeft, ChevronRight, ExternalLink, Hourglass, Settings } from "lucide-react";

const imgUrl = (u) => (u.startsWith("http") ? u : `${API.replace(/\/api$/, "")}${u}`);

const ProductCard = ({ product }) => {
  const [idx, setIdx] = useState(0);
  const images = product.images || [];
  const prev = (e) => { e.stopPropagation(); setIdx((idx - 1 + images.length) % images.length); };
  const next = (e) => { e.stopPropagation(); setIdx((idx + 1) % images.length); };

  return (
    <div
      className="glass-card overflow-hidden cursor-pointer transition-transform hover:-translate-y-1 group"
      onClick={() => window.open(product.link, "_blank", "noopener")}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative" style={{ aspectRatio: "1 / 1", backgroundColor: "rgba(255,255,255,0.03)" }}>
        {images.length > 0 ? (
          <img src={imgUrl(images[idx])} alt={product.name} className="w-full h-full object-cover" data-testid={`product-img-${product.id}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag size={42} style={{ color: "var(--mm-border)" }} />
          </div>
        )}
        {images.length > 1 && (
          <>
            <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 transition-colors hover:bg-black/80"
              style={{ backgroundColor: "rgba(0,0,0,0.55)" }} data-testid={`product-prev-${product.id}`} aria-label="Foto precedente">
              <ChevronLeft size={16} style={{ color: "#fff" }} />
            </button>
            <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 transition-colors hover:bg-black/80"
              style={{ backgroundColor: "rgba(0,0,0,0.55)" }} data-testid={`product-next-${product.id}`} aria-label="Foto successiva">
              <ChevronRight size={16} style={{ color: "#fff" }} />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                  className="w-2 h-2 rounded-full transition-colors"
                  style={{ backgroundColor: i === idx ? "var(--mm-gold)" : "rgba(255,255,255,0.4)" }}
                  data-testid={`product-dot-${product.id}-${i}`} aria-label={`Foto ${i + 1}`} />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-base font-bold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }} data-testid={`product-name-${product.id}`}>{product.name}</h3>
        <div className="flex items-center justify-between">
          <p className="text-lg font-bold gold-text" data-testid={`product-price-${product.id}`}>{product.price}</p>
          <span className="flex items-center gap-1.5 text-xs font-semibold transition-colors group-hover:underline" style={{ color: "var(--mm-gold)" }}>
            Acquista <ExternalLink size={13} />
          </span>
        </div>
      </div>
    </div>
  );
};

export default function Shop() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/shop/products`)
      .then((res) => setData(res.data))
      .catch(() => setData({ enabled: false, products: [] }))
      .finally(() => setLoading(false));
  }, []);

  const comingSoon = data && !data.enabled && !isAdmin;

  return (
    <div className="page-container" data-testid="shop-page">
      <PageHero image={HERO_IMAGES.strumenti} kicker="GoldVision" title="Shop" subtitle="Il merchandising ufficiale GoldVision" />
      <SectionBg type="crypto" />
      <div className="container py-8 relative">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
          </div>
        ) : comingSoon ? (
          <div className="max-w-md mx-auto glass-card p-10 text-center" data-testid="shop-coming-soon">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: "rgba(47,216,168,0.12)" }}>
              <Hourglass size={28} style={{ color: "var(--mm-gold)" }} />
            </div>
            <h2 className="text-2xl font-bold mb-2 gold-text" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>COMING SOON</h2>
            <p className="text-sm" style={{ color: "var(--mm-text-muted)" }}>Lo shop GoldVision sta arrivando. Torna a trovarci presto!</p>
          </div>
        ) : (
          <div className="max-w-6xl mx-auto">
            {isAdmin && (
              <div className="flex flex-wrap items-center justify-between gap-3 mb-8 glass-card px-5 py-4" data-testid="shop-admin-banner">
                <p className="text-sm" style={{ color: "var(--mm-text-secondary)" }}>
                  {data.enabled
                    ? "✅ Lo shop è VISIBILE agli utenti."
                    : "🔒 Lo shop è in Coming Soon per gli utenti: questa è un'anteprima solo per te."}
                </p>
                <Link to="/admin/shop" className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--mm-gold)" }} data-testid="goto-admin-shop">
                  <Settings size={14} /> Gestione Shop
                </Link>
              </div>
            )}
            {data.products.length === 0 ? (
              <div className="glass-card p-10 text-center" data-testid="shop-empty">
                <p style={{ color: "var(--mm-text-muted)" }}>Nessun prodotto disponibile al momento.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shop-grid">
                {data.products.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
